import { CommunicationCategory, CommunicationChannel } from '../enums/Communication.js'
import { CommunicationPreferences } from '../types/Preferences.js'
import { OrgRole } from '../types/OrganizationMembership.js'
import { isParticipantOrgRole } from './organizationMembershipHelpers.js'

// Source of truth for communication categories. Add one here and it shows up in settings.
// `available: false` keeps the enum/labels for later but hides it until the feature ships.
// Audiences reuse OrgRole (normalized to GAMER / FACILITATOR) — no separate audience enum.
export type CommunicationCategoryDef = {
  category: CommunicationCategory
  audiences: OrgRole[]
  labelKey: string
  descriptionKey: string
  available: boolean
}

export const COMMUNICATION_CATEGORY_DEFS: CommunicationCategoryDef[] = [
  {
    category: CommunicationCategory.SPRINT_INVITATION,
    audiences: [OrgRole.GAMER],
    labelKey: 'settings.communication.categories.sprintInvitation.label',
    descriptionKey: 'settings.communication.categories.sprintInvitation.description',
    available: true
  },
  {
    category: CommunicationCategory.SESSION_REMINDER,
    audiences: [OrgRole.GAMER],
    labelKey: 'settings.communication.categories.sessionReminder.label',
    descriptionKey: 'settings.communication.categories.sessionReminder.description',
    available: true
  },
  {
    category: CommunicationCategory.COACH_MESSAGES,
    audiences: [OrgRole.GAMER],
    labelKey: 'settings.communication.categories.coachMessages.label',
    descriptionKey: 'settings.communication.categories.coachMessages.description',
    // Nothing sends under this category yet, so the toggle would control nothing.
    available: false
  },
  {
    category: CommunicationCategory.POST_SESSION_COACH_EMAIL,
    audiences: [OrgRole.GAMER],
    labelKey: 'settings.communication.categories.postSessionCoachEmail.label',
    descriptionKey: 'settings.communication.categories.postSessionCoachEmail.description',
    available: true
  },
  {
    category: CommunicationCategory.LONG_TERM_NUDGE,
    audiences: [OrgRole.GAMER],
    labelKey: 'settings.communication.categories.longTermNudge.label',
    descriptionKey: 'settings.communication.categories.longTermNudge.description',
    // Reserved for the next follow-up campaign; turn on when one actually sends under it.
    available: false
  },
  {
    category: CommunicationCategory.MARKETING,
    audiences: [OrgRole.GAMER, OrgRole.FACILITATOR],
    labelKey: 'settings.communication.categories.marketing.label',
    descriptionKey: 'settings.communication.categories.marketing.description',
    available: false
  }
]

// Union of audiences across all the user's org roles (gamer in one org + facilitator in another → both).
// ADMIN is normalized to FACILITATOR and LEADER to GAMER: staff get facilitator messaging, participants gamer messaging.
export const getCommunicationAudiencesForRoles = (roles: OrgRole[]): OrgRole[] => {
  const audiences = new Set<OrgRole>()
  for (const role of roles) {
    audiences.add(isParticipantOrgRole(role) ? OrgRole.GAMER : OrgRole.FACILITATOR)
  }
  return [...audiences]
}

// Live categories only — unavailable ones are kept in the registry but hidden until built.
export const getAvailableCommunicationCategoryDefs = (): CommunicationCategoryDef[] =>
  COMMUNICATION_CATEGORY_DEFS.filter((def) => def.available)

export const getCommunicationCategoriesForAudiences = (audiences: OrgRole[]): CommunicationCategoryDef[] =>
  getAvailableCommunicationCategoryDefs().filter((def) =>
    def.audiences.some((audience) => audiences.includes(audience))
  )

// Channels surfaced as their own opt-out toggle. SMS ships with SMS sending — none live yet.
export const CHANNEL_OPT_OUT_TOGGLES: CommunicationChannel[] = []

export const isChannelOptedOut = (
  prefs: CommunicationPreferences | undefined,
  channel: CommunicationChannel
): boolean => prefs?.channelOptOuts?.[channel] === true

export const isCategoryOptedOut = (
  prefs: CommunicationPreferences | undefined,
  category: CommunicationCategory
): boolean => prefs?.categoryOptOuts?.[category] === true

// The single check every sender runs before contacting a user.
export const isAllowedToContact = (
  prefs: CommunicationPreferences | undefined,
  target: { category: CommunicationCategory; channel: CommunicationChannel }
): boolean => !isCategoryOptedOut(prefs, target.category) && !isChannelOptedOut(prefs, target.channel)
